import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { functionsService } from '@/services/functionsService';

interface KeyStatus {
  masked: string | null;
  source: 'firestore' | 'env' | 'not_set';
}

interface KeysState {
  openai: KeyStatus;
  fmp: KeyStatus;
  apiNinjas: KeyStatus;
  alphaVantage: KeyStatus;
}

const KEY_META: { field: keyof KeysState; label: string; description: string }[] = [
  { field: 'openai', label: 'OpenAI', description: 'Required for AI analysis, Copilot, and news summarization' },
  { field: 'fmp', label: 'FMP (Financial Modeling Prep)', description: 'Primary source for quotes, statements, metrics, and company data' },
  { field: 'apiNinjas', label: 'API Ninjas', description: 'Fallback source for earnings transcripts and financial data' },
  { field: 'alphaVantage', label: 'Alpha Vantage', description: 'Primary source for earnings call transcripts' },
];

const SOURCE_LABELS: Record<string, { text: string; color: string }> = {
  firestore: { text: 'Admin Panel', color: 'text-green-700 bg-green-100' },
  env: { text: 'Environment / Config', color: 'text-blue-700 bg-blue-100' },
  not_set: { text: 'Not configured', color: 'text-red-700 bg-red-100' },
};

export const ApiKeyManagement: React.FC = () => {
  const [keys, setKeys] = useState<KeysState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [editingField, setEditingField] = useState<string | null>(null);

  const loadStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await functionsService.getApiKeyStatus();
      if (result.success) {
        setKeys(result.keys);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load API key status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const handleSave = async (field: string) => {
    const value = editValues[field]?.trim();
    if (!value) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await functionsService.updateApiKeys({ [field]: value });
      setEditValues((prev) => ({ ...prev, [field]: '' }));
      setEditingField(null);
      setSuccess(`${KEY_META.find((k) => k.field === field)?.label} key updated successfully`);
      await loadStatus();
    } catch (err: any) {
      setError(err.message || 'Failed to update API key');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (field: string) => {
    if (!confirm(`Remove the ${KEY_META.find((k) => k.field === field)?.label} key stored in the Admin Panel?`)) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await functionsService.updateApiKeys({ [field]: '' });
      setSuccess('Key removed');
      await loadStatus();
    } catch (err: any) {
      setError(err.message || 'Failed to remove API key');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blazerex-green"></div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      <Card>
        <p className="body-text text-sm text-gray-500 mb-4">
          API keys set here are stored in Firestore and used as a fallback when no environment variable or Firebase config is set.
          Environment variables and Firebase config always take priority.
        </p>
      </Card>

      {KEY_META.map(({ field, label, description }) => {
        const status = keys?.[field];
        const source = SOURCE_LABELS[status?.source || 'not_set'];
        const isEditing = editingField === field;

        return (
          <Card key={field}>
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="sub-header text-lg">{label}</h3>
                  <p className="body-text text-sm text-gray-500">{description}</p>
                </div>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${source.color}`}>
                  {source.text}
                </span>
              </div>

              {status?.masked && (
                <p className="body-text text-sm font-mono text-gray-600">
                  Current: {status.masked}
                </p>
              )}

              {isEditing ? (
                <div className="flex gap-2">
                  <input
                    type="password"
                    placeholder={`Enter new ${label} key...`}
                    value={editValues[field] || ''}
                    onChange={(e) => setEditValues((prev) => ({ ...prev, [field]: e.target.value }))}
                    className="flex-1 body-text border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blazerex-green focus:border-transparent"
                    autoFocus
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleSave(field)}
                    disabled={saving || !editValues[field]?.trim()}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setEditingField(null); setEditValues((prev) => ({ ...prev, [field]: '' })); }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEditingField(field)}>
                    {status?.masked ? 'Update Key' : 'Set Key'}
                  </Button>
                  {status?.source === 'firestore' && (
                    <Button variant="outline" size="sm" onClick={() => handleRemove(field)}>
                      Remove
                    </Button>
                  )}
                </div>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
};
